import {ConfigObjectType, serializeJSONToString, useApiQuery, TypeWithEtag, useLocationQuery, useLocationNonQuery} from '@wallaby-ui/core';
import {MerieSchema, useAddDynamicItem} from '@wallaby-ui/document';
import * as React from 'react';
import {useEffect, useMemo} from 'react';
import {useNavigate, useParams} from 'react-router-dom';

import {HospitalizationDetailLayout} from './HospitalizationDetailLayout';
import {NotFoundComponentBox} from '../NotFoundComponentBox';

interface OwnProps {
  config: ConfigObjectType;
}

export const HOSPITALIZATIONS_TABS_ID = 'hospitalizations';
export const HOSPITALIZATIONS_TABS_REUSE_DOC_PAGE = 'reuseDocsPage';
type Props = OwnProps;

export const DEFAULT_LAB_CHARTS_FILTERS = {};
export const DEFAULT_TIMELINE_FILTERS = {};
export const DEFAULT_TIMELINE_SORTING = {
  field: 'date',
  direction: 'desc',
};
export const DEFAULT_DOCUMENTS_FILTERS_OPENED = true;
export const DEFAULT_DOCUMENTS_FILTERS = (encounterId: number | undefined) => ({
  hospitalizationIds: encounterId != null ? [encounterId] : null,
});
export const DEFAULT_DOCUMENTS_SORTING = {
  date: 'desc',
};
export const DEFAULT_DOCUMENTS_PAGING = {};

export const USE_PATIENT_V2 = true;
export const HospitalizationDetailLayoutContainer: React.FC<Props> = (props) => {
  const {config, ...restProps} = props;
  const params = useParams();
  const hospitalizationId = params.hospitalizationId ?? '';
  const documentId = params.documentId ?? '';

  const {hospitalizationDetail: hospitalizationDetailUrl} = config.service.url;
  const hospitalizationDetailParams = useMemo(() => ({id: hospitalizationId}), [hospitalizationId]);
  const {
    data: patientCardData,
    isLoading: patientCardIsLoading,
    isError: patientCardIsError,
    refetch: patientCardRefetch,
  } = useApiQuery<TypeWithEtag<MerieSchema.components['schemas']['PatientCardDto']>>(
    [`${hospitalizationDetailUrl}_patientCard`, serializeJSONToString(hospitalizationDetailParams)],
    null,
    {
      axiosConfig: {
        url: `${hospitalizationDetailUrl}/${hospitalizationId}/patient-card`,
      },
    }
  );
  const encounterId = patientCardData?.encounterId;

  const storageIdPrefix = useMemo(() => `/hospitalizedPatients/${hospitalizationId}`, [hospitalizationId]);
  const [query, changeFilters] = useLocationQuery(storageIdPrefix, true, (query) => query.selectedTabs);
  const selectedTabs = query.selectedTabs ?? {};
  const haveDefaultFilters = encounterId != null;
  const filters = useMemo(() => {
    return {
      labCharts: query?.labCharts ?? DEFAULT_LAB_CHARTS_FILTERS,
      timeline: query?.timeline ?? DEFAULT_TIMELINE_FILTERS,
      timelineSorting: query?.timelineSorting === undefined ? DEFAULT_TIMELINE_SORTING : query?.timelineSorting,
      documents: query?.documents ?? DEFAULT_DOCUMENTS_FILTERS(encounterId),
      documentsSorting: query?.documentsSorting ?? DEFAULT_DOCUMENTS_SORTING,
      documentsPaging: query?.documentsPaging ?? DEFAULT_DOCUMENTS_PAGING,
    };
  }, [query, encounterId]);
  const areFiltersOpen = useMemo(
    () =>
      query.areFiltersOpen ?? {
        documents: DEFAULT_DOCUMENTS_FILTERS_OPENED,
      },
    [query]
  );
  const openCloseFilters = React.useCallback(
    (key: string) => {
      changeFilters({
        areFiltersOpen: {
          ...areFiltersOpen,
          [key]: !areFiltersOpen[key],
        },
      });
    },
    [changeFilters, areFiltersOpen]
  );

  const handleSelectedTabChange = (_event: React.SyntheticEvent, tabIdx: number) => {
    changeFilters({
      selectedTabs: {
        [HOSPITALIZATIONS_TABS_ID]: tabIdx,
        [HOSPITALIZATIONS_TABS_REUSE_DOC_PAGE]: true,
      },
    });
  };

  const {
    data: riskFactorData,
    isLoading: riskFactorIsLoading,
    isError: riskFactorIsError,
    refetch: riskFactorsRefetch,
  } = useApiQuery<TypeWithEtag<MerieSchema.components['schemas']['RiskFactorsDto']>>(
    [`${hospitalizationDetailUrl}_riskFactors`, serializeJSONToString(hospitalizationDetailParams)],
    null,
    {
      axiosConfig: {
        url: `${hospitalizationDetailUrl}/${hospitalizationId}/risk-factors`,
      },
    }
  );
  const {nonQuery, setNonQuery} = useLocationNonQuery(storageIdPrefix);
  const oldRiskFactorIds: Set<number> = useMemo(() => {
    return nonQuery.oldRiskFactorIds ?? new Set();
  }, [nonQuery.oldRiskFactorIds]);
  useEffect(() => {
    if (oldRiskFactorIds.size === 0 && riskFactorData?.riskFactors.length)
      setNonQuery({
        oldRiskFactorIds: new Set((riskFactorData?.riskFactors ?? []).map((v) => v.id)),
      });
  }, [oldRiskFactorIds, riskFactorData, setNonQuery]);

  // const queryClient = useQueryClient({context: QueryClientContext});
  const reloadPatientCard = React.useCallback(() => {
    // queryClient.invalidateQueries([config.service.url.hospitalizations])
    patientCardRefetch();
  }, [patientCardRefetch]);
  const reloadRiskFactors = React.useCallback(() => {
    riskFactorsRefetch();
    patientCardRefetch();
  }, [riskFactorsRefetch, patientCardRefetch]);

  const {addDynamicItem} = useAddDynamicItem();
  useEffect(() => {
    if (USE_PATIENT_V2) return;
    addDynamicItem({
      routeId: 'patient',
      id: `${hospitalizationId}`,
      paramsString: `/${hospitalizationId}${documentId ? `/document/${documentId}` : ''}`,
      label: patientCardData?.fullName ?? '',
      isPatient: true,
      cleanLocalStorage: [`${storageIdPrefix}.LQ`, `${storageIdPrefix}.NQ`],
    });
  }, [storageIdPrefix, addDynamicItem, hospitalizationId, documentId, patientCardData?.fullName]);

  const isError = riskFactorIsError && patientCardIsError;
  const isLoading = riskFactorIsLoading && patientCardIsLoading;

  const navigate = useNavigate();
  useEffect(() => {
    if (USE_PATIENT_V2) {
      const redirectPathName = `/hospitalizedPatients/v2/${hospitalizationId}${documentId ? `/document/${documentId}` : ''}`;
      console.warn(`REDIRECTING to "${redirectPathName}"`);
      navigate(redirectPathName);
    }
  }, []); // eslint-disable-line
  if (USE_PATIENT_V2) {
    return <>Redirecting to v2...</>;
  }
  return isError ? (
    <NotFoundComponentBox />
  ) : (
    <HospitalizationDetailLayout
      {...restProps}
      key={hospitalizationId}
      reuseDocsPage={selectedTabs[HOSPITALIZATIONS_TABS_REUSE_DOC_PAGE] ?? false}
      selectedTabIdx={selectedTabs[HOSPITALIZATIONS_TABS_ID] ?? 0}
      handleSelectedTabChange={handleSelectedTabChange}
      isLoading={isLoading}
      riskFactorData={{riskFactors: riskFactorData?.riskFactors ?? [], _etag: riskFactorData?._etag}}
      oldRiskFactorIds={oldRiskFactorIds}
      reloadRiskFactors={reloadRiskFactors}
      config={config}
      patientCardData={patientCardData as TypeWithEtag<MerieSchema.components['schemas']['PatientCardDto']>}
      hospitalizationId={hospitalizationId}
      documentId={documentId}
      encounterId={encounterId}
      reloadPatientCard={reloadPatientCard}
      areFiltersOpen={areFiltersOpen}
      filters={filters}
      haveDefaultFilters={haveDefaultFilters}
      changeFilters={(newFilterData) =>
        changeFilters({
          ...newFilterData,
          selectedTabs: {
            ...selectedTabs,
            ...(newFilterData.selectedTabs ?? {}),
            [HOSPITALIZATIONS_TABS_REUSE_DOC_PAGE]: true,
          },
        })
      }
      openCloseFilters={openCloseFilters}
      storageIdPrefix={storageIdPrefix}
    />
  );
};
